import { Colors } from "../../utils/style/colors";
import { moduleIds } from "../../utils/models/Module";

const defaultTile = {
  disabled: () => false,
  wsTopics: ["*"],
  sanitizeForTemplate: (config) => config,
  needsConfiguration: () => false,
};

const sanitizeRefArrays = (config) => ({
  ...config,
  devices: [],
  teams: [],
  geofences: [],
});

export const getTiles = (t) => [
  {
    ...defaultTile,
    type: "geofenceAttendance",
    title: t("attendanceOverview"),
    description: t("geofenceAttendance.tile.description"),
    icon: "GeofenceIcon",
    color: Colors.geofence,
    config: "geofenceAttendanceConfig",
    wsTopics: [
      "geofence",
      "geofences",
      "device",
      "devices",
      "geofencePresence",
      "site",
      "sites",
    ],
    sanitizeForTemplate: (config) => ({ ...config, geofenceId: null }),
    needsConfiguration: (config) => !config?.geofenceId,
  },
  {
    ...defaultTile,
    type: "freeText",
    title: t("freeText"),
    description: t("freeText.tile.description"),
    icon: "TextIcon",
    color: Colors.gray20,
    config: "freeTextConfig",
    wsTopics: [],
  },
  {
    ...defaultTile,
    type: "liveMap",
    title: t("liveMap"),
    description: t("liveMap.tile.description"),
    icon: "MapIcon",
    color: Colors.lightBlue,
    config: "liveMapConfig",
    wsTopics: [
      "geofence",
      "geofences",
      "device",
      "devices",
      "geofencePresence",
      "site",
      "sites",
      "team",
      "teams",
    ],
    sanitizeForTemplate: sanitizeRefArrays,
    needsConfiguration: (config) =>
      !config?.devices?.length &&
      !config?.teams?.length &&
      !config?.geofences?.length,
  },
  {
    ...defaultTile,
    type: "teamDevicesByStatus",
    title: t("teamDevicesByStatus"),
    disabled: ({ user }) => !user.hasModule(moduleIds.Teams),
    description: t("teamDevicesByStatus.tile.description"),
    icon: "DevicesIcon",
    color: Colors.lightBlue,
    config: "teamDevicesByStatusConfig",
    wsTopics: ["device", "devices", "team", "teams"],
    sanitizeForTemplate: (config) => ({ ...config, teamId: null }),
    needsConfiguration: (config) => !config?.teamId,
  },
  {
    ...defaultTile,
    type: "deviceInformation",
    title: t("deviceInformation"),
    description: t("deviceInformation.tile.description"),
    icon: "DevicesIcon",
    color: Colors.secondary(),
    config: "deviceInformationConfig",
    wsTopics: ["device", "devices", "team", "teams"],
    sanitizeForTemplate: (config) => ({ ...config, deviceId: null }),
  },
  {
    ...defaultTile,
    type: "startScenario",
    title: t("startScenario"),
    description: t("startScenario.tile.description"),
    icon: "ButtonIcon",
    color: Colors.secondary(),
    config: "selectScenario",
    moduleId: moduleIds.ScenarioTile,
    wsTopics: ["scenario", "scenarios"],
    sanitizeForTemplate: (config) => ({
      ...config,
      scenarioId: null,
      scenarioIds: [],
    }),
    needsConfiguration: (config) =>
      !config?.scenarioId && !config?.scenarioIds?.length,
  },
  {
    ...defaultTile,
    type: "scenarioToggle",
    title: t("scenarioToggle"),
    description: t("scenarioToggleTileDescription"),
    icon: "ScenarioIcon",
    color: Colors.secondary(),
    config: "scenarioToggleConfig",
    moduleId: moduleIds.ScenarioTile,
    wsTopics: ["scenario", "scenarios"],
    sanitizeForTemplate: (config) => ({ ...config, scenarioIds: [] }),
  },
  {
    ...defaultTile,
    type: "startAssistanceRequest",
    title: t("newAssistanceRequest"),
    description: t("startAssistanceRequest.tile.description"),
    icon: "AssistanceRequestIcon",
    color: Colors.secondary(),
    config: "assistanceRequestConfig",
    moduleId: moduleIds.AssistanceRequests,
    // Assistance requests are V3-only — off for V4-only accounts.
    disabled: ({ user }) => user.isV4Only,
    wsTopics: [
      "device",
      "devices",
      "assistanceRequest",
      "assistanceRequests",
      "team",
      "teams",
      "geofence",
      "geofences",
      "quickTexts",
    ],
    sanitizeForTemplate: (config) => ({
      ...sanitizeRefArrays(config),
      followUpScenarioId: null,
    }),
    needsConfiguration: (config) =>
      !config?.devicesEditable &&
      !config?.teamsEditable &&
      !config?.geofencesEditable &&
      !config?.devices?.length &&
      !config?.teams?.length &&
      !config?.geofences?.length,
  },
  {
    ...defaultTile,
    type: "pendingAssistanceRequests",
    title: t("pendingAssistanceRequests"),
    description: t("pendingAssistanceRequests.tile.description"),
    icon: "AssistanceRequestIcon",
    color: Colors.primary(),
    // Assistance requests are V3-only — off for V4-only accounts.
    disabled: ({ user }) => user.isV4Only,
    wsTopics: [
      "device",
      "devices",
      "assistanceRequest",
      "assistanceRequests",
      "team",
      "teams",
      "geofence",
      "geofences",
      "quickTexts",
    ],
  },
  {
    ...defaultTile,
    type: "openAlarms",
    title: t("openAlarms"),
    config: "openAlarmsConfig",
    description: t("openAlarms.tile.description"),
    icon: "AlarmIcon",
    color: Colors.primary(),
    wsTopics: [
      "alarm",
      "alarms",
      "device",
      "devices",
      "geofence",
      "geofences",
      "protocol",
      "protocols",
      "contact",
      "contacts",
      "site",
      "sites",
      "geofencePresence",
    ],
  },
  {
    ...defaultTile,
    type: "openAlerts",
    title: t("openAlerts"),
    description: t("openAlerts.tile.description"),
    icon: "AlarmIcon",
    color: Colors.primary(),
    wsTopics: ["event", "events", "alarm", "alarms"],
  },
  {
    ...defaultTile,
    type: "daysWithoutAlarm",
    title: t("daysWithoutAlarm"),
    description: t("daysWithoutAlarm.tile.description"),
    icon: "WithoutAlarmIcon",
    color: Colors.success,
    wsTopics: ["alarms", "alarm"],
  },
  {
    ...defaultTile,
    type: "credits",
    title: t("credits"),
    description: t("credits.tile.description"),
    icon: "CreditsIcon",
    color: Colors.primary(),
    wsTopics: ["credits"],
  },
  {
    ...defaultTile,
    type: "incompleteDevices",
    title: t("incompleteDevices"),
    description: t("incompleteDevices.tile.description"),
    icon: "IncompleteDeviceIcon",
    color: Colors.warning,
    wsTopics: ["device", "devices"],
  },
  {
    ...defaultTile,
    type: "unsupportedFirmware",
    title: t("updateNeeded"),
    description: t("unsupportedFirmware.tile.description"),
    icon: "UpgradeIcon",
    color: Colors.warning,
    wsTopics: ["device", "devices"],
  },
  {
    ...defaultTile,
    type: "trial",
    title: t("trial"),
    description: t("trial.tile.description"),
    icon: "TrialIcon",
    color: Colors.success,
    wsTopics: [],
  },
  {
    ...defaultTile,
    type: "iframe",
    disabled: ({ adminMode }) => !adminMode,
    title: t("iframe"),
    description: t("iframe.tile.description"),
    icon: "IframeIcon",
    color: Colors.gray20,
    config: "iframeConfig",
    wsTopics: [],
  },
];
